import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { api } from '../../services/api.js';
import { PoS } from '../../types/index.js';
import { StoreTypeSelector } from './StoreTypeSelector.js';
import { AddressForm } from '../shared/AddressForm.js';
import { DistrictAutocomplete } from '../shared/DistrictAutocomplete.js';
import { DistributorSelector } from '../customers/DistributorSelector.js';
import {
  InvoiceBillingSection,
  invoiceAddressPayload,
} from '../customers/InvoiceBillingSection.js';
import {
  NEW_POS_FORM_DEFAULTS,
  toNewPosFormDraft,
  type NewPosDistributorDraft,
  type NewPosFormDraft,
} from '../../utils/activeVisitDraft.js';

const posSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  store_type: z.string().min(1, 'Store type is required'),
  street: z.string().min(1, 'Street is required'),
  street_number: z.string().min(1, 'Street number is required'),
  postal_code: z.string().min(4, 'Postal code is required'),
  city: z.string().min(1, 'City is required'),
  country: z.string().optional(),
  district: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  invoice_email: z.string().email().optional().or(z.literal('')),
  owner_name: z.string().optional(),
  invoice_name: z.string().optional(),
  invoice_street: z.string().optional(),
  invoice_street_number: z.string().optional(),
  invoice_postal_code: z.string().optional(),
  invoice_city: z.string().optional(),
  invoice_country: z.string().optional(),
  contact_person_name: z.string().optional(),
  contact_person_phone: z.string().optional(),
  contact_person_email: z.string().email().optional().or(z.literal('')),
  contact_availability_time: z.string().optional(),
  estimated_traffic: z.string().optional(),
  notes: z.string().optional(),
  tax_number: z.string().max(50).optional(),
  vat_id: z.string().max(50).optional(),
  is_premium: z.boolean().optional().default(false),
});

type PosFormValues = z.infer<typeof posSchema>;

interface InlinePoSFormProps {
  onCreated: (pos: PoS) => void;
  initialValues?: Partial<NewPosFormDraft> | null;
  initialDistributors?: NewPosDistributorDraft[];
  onDraftChange?: (
    values: NewPosFormDraft,
    distributors: NewPosDistributorDraft[]
  ) => void;
}

export const InlinePoSForm: React.FC<InlinePoSFormProps> = ({
  onCreated,
  initialValues,
  initialDistributors,
  onDraftChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [distributorLinks, setDistributorLinks] = useState<any[]>(
    initialDistributors ?? []
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    formState: { errors },
  } = useForm<PosFormValues>({
    resolver: zodResolver(posSchema),
    defaultValues: {
      ...NEW_POS_FORM_DEFAULTS,
      ...initialValues,
    },
  });

  useEffect(() => {
    const publish = () => {
      onDraftChange?.(
        toNewPosFormDraft(getValues()),
        distributorLinks as NewPosDistributorDraft[]
      );
    };
    publish();
    const subscription = watch(() => publish());
    return () => subscription.unsubscribe();
  }, [watch, getValues, distributorLinks, onDraftChange]);

  const isPremium = watch('is_premium');

  const onSubmit = async (data: PosFormValues) => {
    setLoading(true);
    try {
      const payload = {
        name: data.name,
        store_type: data.store_type,
        street: data.street,
        street_number: data.street_number,
        postal_code: data.postal_code,
        city: data.city,
        country: data.country || 'Germany',
        district: data.district || undefined,
        phone: data.phone || undefined,
        email: data.email || undefined,
        invoice_email: data.invoice_email || undefined,
        owner_name: data.owner_name || undefined,
        ...invoiceAddressPayload(data),
        contact_person_name: data.contact_person_name || undefined,
        contact_person_phone: data.contact_person_phone || undefined,
        contact_person_email: data.contact_person_email || undefined,
        contact_availability_time: data.contact_availability_time || undefined,
        estimated_traffic: data.estimated_traffic || undefined,
        notes: data.notes || undefined,
        tax_number: data.tax_number || undefined,
        vat_id: data.vat_id || undefined,
        is_premium: data.is_premium ?? false,
      };

      const res = await api.post('/pos', payload);
      const customerId = res.data.id;

      for (const link of distributorLinks) {
        try {
          await api.post('/distributors/links', {
            pos_id: customerId,
            distributor_id: link.distributor_id,
            pos_number_at_distributor: link.pos_number_at_distributor || '',
            is_primary: link.is_primary,
          });
        } catch (err) {
          console.error('Distributor link failed:', err);
        }
      }

      toast.success('PoS created');
      onCreated(res.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to create PoS');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6 bg-white p-6 rounded-2xl shadow-lg"
    >
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Store Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('name')}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          placeholder="e.g. Corner Shop"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <StoreTypeSelector
          value={watch('store_type')}
          onChange={(value) => setValue('store_type', value)}
        />
        {errors.store_type && (
          <p className="mt-1 text-sm text-red-600">{errors.store_type.message}</p>
        )}
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Address</h3>
        <AddressForm register={register} errors={errors} required={true} />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          District/City Area
        </label>
        <DistrictAutocomplete
          value={watch('district') || ''}
          onChange={(value) => setValue('district', value)}
          city={watch('city')}
          placeholder="e.g. Kreuzberg, Mitte"
        />
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone</label>
              <input
                type="tel"
                {...register('phone')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="+49 30 12345678"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
              <input
                type="email"
                {...register('email')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="kontakt@beispiel.de"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Contact Person Name
              </label>
              <input
                type="text"
                {...register('contact_person_name')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="z.B. Max Mustermann"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Contact Person Phone
              </label>
              <input
                type="tel"
                {...register('contact_person_phone')}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="+49 170 1234567"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Contact Person Email
            </label>
            <input
              type="email"
              {...register('contact_person_email')}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="max@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Contact Person Availability
            </label>
            <div className="flex gap-3">
              {[
                { value: 'morning', label: 'Morning', emoji: '🌅' },
                { value: 'afternoon', label: 'Afternoon', emoji: '☀️' },
                { value: 'evening', label: 'Evening', emoji: '🌙' },
              ].map((time) => {
                const currentValue = watch('contact_availability_time') || '';
                const values = currentValue.split(',').filter(Boolean);
                const isSelected = values.includes(time.value);
                return (
                  <button
                    key={time.value}
                    type="button"
                    onClick={() => {
                      const newValues = isSelected
                        ? values.filter((v) => v !== time.value)
                        : [...values, time.value];
                      setValue('contact_availability_time', newValues.join(','));
                    }}
                    className={`flex-1 min-h-[56px] px-4 py-3 rounded-lg font-semibold text-sm transition-all ${
                      isSelected
                        ? 'bg-blue-500 text-white shadow-md border-2 border-blue-600'
                        : 'bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <div className="text-xl mb-1">{time.emoji}</div>
                    {time.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">Multiple selections allowed</p>
          </div>
        </div>
      </div>

      <InvoiceBillingSection
        register={register}
        errors={errors}
        setValue={setValue}
        watch={watch}
      />

      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Distributors</h3>
        <DistributorSelector value={distributorLinks} onChange={setDistributorLinks} />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
          Estimated Customer Traffic
        </label>
        <select
          {...register('estimated_traffic')}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        >
          <option value="">-- Select --</option>
          <option value="low">Low (&lt; 50/day)</option>
          <option value="medium">Medium (50-200/day)</option>
          <option value="high">High (200-500/day)</option>
          <option value="very_high">Very High (&gt; 500/day)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notes</label>
        <textarea
          {...register('notes')}
          rows={3}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
          placeholder="Additional information..."
        />
      </div>

      <div className="border border-gray-200 rounded-xl p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">Premium Location</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Flag this PoS as a priority location for sampling and first placements.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setValue('is_premium', !isPremium)}
          className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-colors ${
            isPremium
              ? 'border-amber-400 bg-amber-50 text-amber-700'
              : 'border-gray-200 bg-white text-gray-500 hover:border-amber-300'
          }`}
        >
          <Star size={16} className={isPremium ? 'fill-amber-400 text-amber-400' : ''} />
          {isPremium ? 'Premium' : 'Standard'}
        </button>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors disabled:opacity-50"
      >
        {loading ? 'Creating...' : 'Create PoS & Continue'}
      </button>
    </form>
  );
};
